import { Sparkles, Plus, WifiOff, Wifi, Loader2, ArrowUpRight } from "lucide-react";
import posthog from "@/lib/posthog";
import { toast } from "sonner";
import { useState, useContext, useEffect, useMemo } from "react";
import MainSectionSection from "../../components/MainSectionSection";
import AgentCard from "../../components/agent/AgentCard";
import PlaceholderAgentCard from "../../components/agent/PlaceholderAgentCard"; // TODO: Remove this import when placeholders are removed
import CreateAgentFlow from "../../components/agent/CreateAgentFlow";
import MerchantContext from "../../contexts/MerchantContext";
import { trpc } from "../../trpc";
import { formatDateWithTimezone } from "../../../../agent/src/common/time";
import type { ToolType } from "../../types";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const AgentsConfigurationContainer = () => {
  const {
    merchantId,
    merchantType,
    selfConfigEnabled,
    country,
    isLoading: isLoadingContext,
  } = useContext(MerchantContext);
  const isMobile = useIsMobile();
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTools, setSelectedTools] = useState<ToolType[]>([]);
  const [originalTools, setOriginalTools] = useState<ToolType[]>([]);

  // Agent creation flow state
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [isVersionsModalOpen, setIsVersionsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isWhatsappModalOpen, setIsWhatsappModalOpen] = useState(false);

  const [agentToConnect, setAgentToConnect] = useState<string | null>(null);
  const [agentBeingSetAsPrimary, setAgentBeingSetAsPrimary] = useState<string | null>(null);

  // Project linking modal (development merchants only)
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [agentToLinkProject, setAgentToLinkProject] = useState<string | null>(null);

  // Development migration (legacy → development-v2) state
  const [isMigrateModalOpen, setIsMigrateModalOpen] = useState(false);
  const [migratingAgentId, setMigratingAgentId] = useState<string | null>(null);
  const isPreMigrationDevelopment = merchantType === "development" && !selfConfigEnabled;

  const { data: agents, isLoading } = trpc.agents.list.useQuery(
    { merchantId },
    {
      // Pre-migration development merchants also need the agents list so they can
      // pick which agent to migrate. Other pre-V2 merchants stay gated.
      enabled: !!merchantId && (selfConfigEnabled || isPreMigrationDevelopment),
    }
  );

  const { data: pipelinesList } = trpc.pipeline.list.useQuery(
    { merchantId: merchantId ?? "" },
    { enabled: !!merchantId }
  );

  const pipelineDisplayNameById = useMemo(
    () =>
      (pipelinesList ?? []).reduce<Record<string, string>>((acc, p) => {
        acc[p.id] = p.name;
        return acc;
      }, {}),
    [pipelinesList]
  );

  // For development merchants, load projects so we can render the linked-project label
  // on each agent card.
  const { data: projectsList } = trpc.projects.list.useQuery(
    { merchantId: merchantId ?? "" },
    { enabled: !!merchantId && merchantType === "development" }
  );

  const projectNameById = useMemo(
    () =>
      (projectsList ?? []).reduce<Record<string, string>>((acc, p) => {
        acc[p.id] = p.name;
        return acc;
      }, {}),
    [projectsList]
  );

  const handleAgentSelect = (agentId: string) => {
    // Development merchants may have legacy agents that were never migrated to V2
    // (no assistantId). Selecting them would call `getAssistantDetails` which
    // throws. Instead, route the click to the migration modal so the user can
    // self-serve the remaining migrations.
    const target = agents?.find((a) => a.id === agentId);
    if (target && !target.assistantId) {
      setMigratingAgentId(agentId);
      setIsMigrateModalOpen(true);
      return;
    }
    setSelectedAgentId(selectedAgentId === agentId ? null : agentId);
    setIsEditing(false); // Reset edit mode when selecting a different agent
    // Reset tools state when selecting a different agent
    setSelectedTools([]);
    setOriginalTools([]);
  };

  const handleEditClick = () => {
    if (assistantDetails) {
      const currentPrompt = assistantDetails.context.systemPrompt;
      const currentTools: ToolType[] = (assistantDetails.context.tools || []) as ToolType[];
      setOriginalPrompt(currentPrompt);
      setEditedPrompt(currentPrompt);
      setOriginalTools(currentTools);
      setSelectedTools(currentTools);
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    setIsConfirmModalOpen(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedPrompt("");
    setOriginalPrompt("");
    setDescription("");
    setSelectedTools([]);
    setOriginalTools([]);
  };

  const handleConfirmSave = () => {
    if (!selectedAgentId || !description.trim() || !editedPrompt.trim()) return;

    // Filter out tools that are not supported by the backend
    const supportedTools = [
      "transferConversation",
      "scheduleVisit",
      "getAvailableSlots",
      "getProperty",
      "getPropertyByCode",
      "searchProperties",
      "assignAgent",
      "setEmail",
      "registerListingRequest",
      "discardLead",
      "calculateDaysFor",
      "createMaintenanceRequest",
      "getCustomerProperties",
      "getFloorPlan",
      "createServiceRequest",
      "getCustomerDocuments",
      "updatePipelinePosition",
      "lookupByContractNumber",
      "setAppointmentIntention",
      "sendMedia",
      "requestRentGuaranteePreApproval",
      // Development-v2 tools
      "getProject",
      "searchProjects",
      "typeOfProject",
      "updateLeadProfile",
    ] as const;

    const filteredTools = selectedTools.filter((tool) =>
      supportedTools.includes(tool as any)
    ) as Array<(typeof supportedTools)[number]>;

    updateAssistantMutation.mutate({
      merchantId,
      agentId: selectedAgentId,
      description: description.trim(),
      systemPrompt: editedPrompt.trim(),
      tools: filteredTools,
    });
  };

  const handleCreateAgentClick = () => {
    setIsCreatingAgent(true);
  };

  const handleBackToAgentsList = () => {
    setIsCreatingAgent(false);
  };

  const handleAgentCreated = () => {
    posthog.capture("agent created", { merchant_id: merchantId });
    handleBackToAgentsList();
  };

  const handleModalCancel = () => {
    setDescription("");
    setIsConfirmModalOpen(false);
  };

  const handleConnectWhatsappClick = (agentId: string) => {
    setAgentToConnect(agentId);
    setIsWhatsappModalOpen(true);
  };

  const handleWhatsappConnectionSelect = (whatsappConnectionId: string | null) => {
    if (!agentToConnect) return;

    updateWhatsappConnectionMutation.mutate({
      merchantId,
      agentId: agentToConnect,
      whatsappConnectionId,
    });
  };

  const handleLinkProjectClick = (agentId: string) => {
    setAgentToLinkProject(agentId);
    setIsProjectModalOpen(true);
  };

  const handleProjectSelect = (projectId: string | null) => {
    if (!agentToLinkProject) return;
    updateAgentProjectMutation.mutate({
      merchantId,
      agentId: agentToLinkProject,
      projectId,
    });
  };

  // Check if the prompt has been modified and is not empty
  const hasPromptChanged =
    editedPrompt.trim() !== originalPrompt.trim() && editedPrompt.trim() !== "";
  const isValidPrompt = editedPrompt.trim() !== "";

  // Check if tools have changed
  const toolsChanged =
    originalTools.length !== selectedTools.length ||
    !originalTools.every((tool) => selectedTools.includes(tool)) ||
    !selectedTools.every((tool) => originalTools.includes(tool));

  // Check if there are any changes
  const hasChanges = hasPromptChanged || toolsChanged;

  // Fetch assistant details when an agent is selected. Only enabled when the
  // selected agent actually has an assistantId — un-migrated dev agents would
  // throw on this call.
  const selectedAgent = useMemo(
    () => agents?.find((a) => a.id === selectedAgentId),
    [agents, selectedAgentId]
  );
  const { data: assistantDetails, isLoading: isLoadingAssistant } =
    trpc.agents.getAssistantDetails.useQuery(
      { merchantId, agentId: selectedAgentId! },
      {
        enabled: !!merchantId && !!selectedAgentId && !!selectedAgent?.assistantId,
      }
    );

  // Initialize tools when assistant details are loaded (if not editing)
  useEffect(() => {
    if (assistantDetails && !isEditing) {
      const currentTools: ToolType[] = (assistantDetails.context.tools || []) as ToolType[];
      setSelectedTools(currentTools);
      setOriginalTools(currentTools);
    }
  }, [assistantDetails, isEditing]);

  // Fetch assistant versions when the modal is opened
  const { data: assistantVersions, isLoading: isLoadingVersions } =
    trpc.agents.getAssistantVersions.useQuery(
      { merchantId, agentId: selectedAgentId! },
      {
        enabled: !!merchantId && !!selectedAgentId && isVersionsModalOpen,
      }
    );

  const utils = trpc.useUtils();

  // Fetch whatsapp connections when the modal is opened
  const { data: whatsappConnections, isLoading: isLoadingWhatsappConnections } =
    trpc.agents.listWhatsappConnections.useQuery(
      { merchantId },
      {
        enabled: !!merchantId && isWhatsappModalOpen,
      }
    );

  // Mutation for updating assistant
  const updateAssistantMutation = trpc.agents.updateAssistant.useMutation({
    onSuccess: () => {
      posthog.capture("agent prompt updated", {
        merchant_id: merchantId,
        agent_id: selectedAgentId,
      });
      setIsEditing(false);
      setDescription("");
      setIsConfirmModalOpen(false);
      // Invalidate and refetch assistant details to get the updated data
      utils.agents.getAssistantDetails.invalidate({
        merchantId,
        agentId: selectedAgentId!,
      });
    },
    onError: (error) => {
      console.error("Error updating assistant:", error);
    },
  });

  // Mutation for updating agent whatsapp connection
  const updateWhatsappConnectionMutation = trpc.agents.updateAgentWhatsappConnection.useMutation({
    onSuccess: () => {
      setIsWhatsappModalOpen(false);
      setAgentToConnect(null);
      // Invalidate and refetch agents list to get updated connection status
      utils.agents.list.invalidate({ merchantId });
    },
    onError: (error) => {
      console.error("Error updating whatsapp connection:", error);
    },
  });

  // Mutation for linking / unlinking a project on an agent (development merchants only)
  const updateAgentProjectMutation = trpc.agents.updateAgentProject.useMutation({
    onSuccess: () => {
      setIsProjectModalOpen(false);
      setAgentToLinkProject(null);
      utils.agents.list.invalidate({ merchantId });
      toast.success("Proyecto actualizado");
    },
    onError: (error) => {
      console.error("Error updating agent project:", error);
      toast.error(error.message || "Error al vincular el proyecto");
    },
  });

  // Mutation for migrating a development merchant to the development-v2 graph
  const migrateToV2Mutation = trpc.agents.migrateToV2.useMutation({
    onSuccess: () => {
      posthog.capture("agent migrated to development-v2", {
        merchant_id: merchantId,
        agent_id: migratingAgentId,
      });
      toast.success("Migración a V2 completada. Recargando…");
      setIsMigrateModalOpen(false);
      setMigratingAgentId(null);
      // The merchant context reads `selfConfigEnabled` once on mount; a reload
      // is the simplest way to ensure the whole page re-evaluates against the
      // new state.
      setTimeout(() => window.location.reload(), 800);
    },
    onError: (error) => {
      console.error("Error migrating agent to V2:", error);
      toast.error(`No se pudo migrar el agente: ${error.message}`);
      setMigratingAgentId(null);
    },
  });

  const handleConfirmMigrate = () => {
    if (!migratingAgentId) return;
    migrateToV2Mutation.mutate({
      merchantId,
      agentId: migratingAgentId,
    });
  };

  // Mutation for setting primary agent
  const setPrimaryAgentMutation = trpc.agents.setPrimaryAgent.useMutation({
    onSuccess: () => {
      // Invalidate and refetch agents list to get updated primary status
      utils.agents.list.invalidate({ merchantId });
      setAgentBeingSetAsPrimary(null);
    },
    onError: (error) => {
      console.error("Error setting primary agent:", error);
      setAgentBeingSetAsPrimary(null);
    },
  });

  const handleSetPrimary = (agentId: string) => {
    setAgentBeingSetAsPrimary(agentId);
    setPrimaryAgentMutation.mutate({
      merchantId,
      agentId,
    });
  };

  // Group agents by whatsappConnectionId
  const groupedAgents = agents
    ? agents.reduce((acc, agent) => {
        const key = agent.whatsappConnectionId || "unconnected";
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(agent);
        return acc;
      }, {} as Record<string, typeof agents>)
    : {};

  const connectedGroups = Object.entries(groupedAgents).filter(([key]) => key !== "unconnected");
  const unconnectedAgents = groupedAgents["unconnected"] || [];

  // Wait for context to load before rendering anything to prevent flash/remount
  if (isLoadingContext) {
    return (
      <MainSectionSection gap="24px">
        <Skeleton className="min-h-[400px] h-full rounded-xl" />
      </MainSectionSection>
    );
  }

  // Render construction message if selfConfigEnabled is false
  if (!selfConfigEnabled) {
    // Development merchants get a migration CTA instead of the static message.
    if (isPreMigrationDevelopment) {
      return (
        <MainSectionSection gap="24px">
          <div className="rounded-md bg-white p-6 shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)]">
            <h2 className="text-lg font-bold md:text-xl">Migración a V2</h2>
            <p className="mt-2 text-sm text-gray-600">
              Este merchant de tipo <strong>development</strong> aún no ha sido migrado a la nueva
              versión del agente. La migración generará un asistente en LangGraph con el prompt
              actual congelado a partir del <code>agent.config</code> existente.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Después de migrar, los campos del prompt en <code>agent.config</code> dejarán de ser
              leídos en tiempo de ejecución. Las ediciones futuras se harán sobre el asistente de
              LangGraph desde esta pantalla.
            </p>

            <div className="mt-4">
              {isLoading ? (
                <Skeleton className="h-10 w-full max-w-sm" />
              ) : agents && agents.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {agents.map((agent) => (
                    <div
                      key={agent.id}
                      className="flex flex-col gap-2 rounded-md border border-gray-200 p-3 md:flex-row md:items-center md:justify-between"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {agent.agentName || agent.id}
                        </span>
                        <span className="text-xs text-gray-500">
                          {agent.topic ?? "development-sales"} · {agent.id}
                        </span>
                      </div>
                      <Button
                        onClick={() => {
                          setMigratingAgentId(agent.id);
                          setIsMigrateModalOpen(true);
                        }}
                        disabled={migrateToV2Mutation.isLoading}
                      >
                        <ArrowUpRight className="size-4" />
                        Migrar a V2
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No se encontraron agentes para este merchant.
                </p>
              )}
            </div>
          </div>

          <Dialog open={isMigrateModalOpen} onOpenChange={setIsMigrateModalOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmar migración a V2</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  Esta acción creará (o actualizará) un asistente en el grafo{" "}
                  <code>development-v2</code>, congelará el prompt actual a partir del{" "}
                  <code>agent.config</code> y activará <code>selfConfigEnabled</code> para este
                  merchant.
                </p>
                <p>
                  El rollback se hace cambiando <code>selfConfigEnabled</code> a <code>false</code>{" "}
                  manualmente desde DynamoDB.
                </p>
                <p>¿Confirmas que quieres continuar?</p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsMigrateModalOpen(false)}
                  disabled={migrateToV2Mutation.isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmMigrate}
                  disabled={migrateToV2Mutation.isLoading || !migratingAgentId}
                >
                  {migrateToV2Mutation.isLoading ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Migrando…
                    </>
                  ) : (
                    "Migrar"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </MainSectionSection>
      );
    }

    return (
      <MainSectionSection gap="24px">
        <div>
          <div className="rounded-md bg-white p-6 shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)]">
            <p className="text-gray-600">
              Seccion en construccion. Aqui podras configurar los agentes de tu negocio.
            </p>
          </div>
        </div>
      </MainSectionSection>
    );
  }

  // Render agent creation flow
  if (isCreatingAgent) {
    return <CreateAgentFlow onCancel={handleBackToAgentsList} onSuccess={handleAgentCreated} />;
  }

  // Render main agents list
  return (
    <MainSectionSection gap="24px">
      <div className="flex flex-col gap-6">
        {/* Header with Create Agent button - always visible */}
        {!isLoading && (
          <div className="flex w-full items-center justify-between">
            <h2 className="text-xl font-bold md:text-2xl">Agentes</h2>
            <Button onClick={handleCreateAgentClick}>
              <Plus className="size-4" />
              Crear Agente
            </Button>
          </div>
        )}

        {isLoading ? (
          <Skeleton className="min-h-[400px] h-full rounded-xl" />
        ) : (
          <>
            {agents && agents.length > 0 ? (
              <div className="flex flex-col gap-8">
                {/* Connected Agents - Grouped by WhatsApp Connection */}
                {connectedGroups.map(([connectionId, groupAgents]) => {
                  const phoneNumber = groupAgents[0]?.whatsappPhoneNumber || "Sin numero";
                  const hasMultipleAgents = groupAgents.length > 1;

                  return (
                    <div
                      key={connectionId}
                      className="rounded-xl border-2 border-green-200 bg-green-50 p-6 transition-all hover:border-green-300 hover:shadow-[0px_4px_12px_rgba(0,0,0,0.08)]"
                    >
                      <div className="mb-4 flex items-center gap-3">
                        <Wifi className="size-5 text-green-500" />
                        <span className="text-lg font-bold text-gray-800">
                          WhatsApp: {phoneNumber}
                        </span>
                        {hasMultipleAgents && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                            {groupAgents.length} agentes
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {groupAgents.map((agent) => (
                          <AgentCard
                            key={agent.id}
                            agent={agent}
                            pipelineDisplayNameById={pipelineDisplayNameById}
                            projectName={
                              agent.projectId ? projectNameById[agent.projectId] : undefined
                            }
                            showProjectButton={merchantType === "development"}
                            showMigrateButton={
                              merchantType === "development" && !agent.assistantId
                            }
                            isMigrating={
                              migratingAgentId === agent.id && migrateToV2Mutation.isLoading
                            }
                            isSelected={selectedAgentId === agent.id}
                            onClick={() => handleAgentSelect(agent.id)}
                            onConnectClick={() => handleConnectWhatsappClick(agent.id)}
                            onProjectClick={() => handleLinkProjectClick(agent.id)}
                            onMigrateClick={() => {
                              setMigratingAgentId(agent.id);
                              setIsMigrateModalOpen(true);
                            }}
                            onSetPrimary={() => handleSetPrimary(agent.id)}
                            showPrimarySelector={hasMultipleAgents}
                            primaryGroupName={phoneNumber}
                            isSettingPrimary={
                              agentBeingSetAsPrimary === agent.id &&
                              setPrimaryAgentMutation.isLoading
                            }
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Unconnected Agents */}
                {unconnectedAgents.length > 0 && (
                  <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-6">
                    <div className="mb-4 flex items-center gap-3">
                      <WifiOff className="size-5 text-gray-400" />
                      <span className="text-lg font-bold text-gray-600">
                        Agentes sin conexion de WhatsApp
                      </span>
                      <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-700">
                        {unconnectedAgents.length}{" "}
                        {unconnectedAgents.length === 1 ? "agente" : "agentes"}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {unconnectedAgents.map((agent) => (
                        <AgentCard
                          key={agent.id}
                          agent={agent}
                          pipelineDisplayNameById={pipelineDisplayNameById}
                          projectName={
                            agent.projectId ? projectNameById[agent.projectId] : undefined
                          }
                          showProjectButton={merchantType === "development"}
                          showMigrateButton={
                            merchantType === "development" && !agent.assistantId
                          }
                          isMigrating={
                            migratingAgentId === agent.id && migrateToV2Mutation.isLoading
                          }
                          isSelected={selectedAgentId === agent.id}
                          onClick={() => handleAgentSelect(agent.id)}
                          onConnectClick={() => handleConnectWhatsappClick(agent.id)}
                          onProjectClick={() => handleLinkProjectClick(agent.id)}
                          onMigrateClick={() => {
                            setMigratingAgentId(agent.id);
                            setIsMigrateModalOpen(true);
                          }}
                          showPrimarySelector={false}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* TODO: Remove placeholder agents once real agents are implemented */}
                {merchantId === "reda" && (
                  <>
                    <Separator />
                    <div>
                      <p className="mb-4 text-lg font-bold text-gray-600">
                        Agentes de ejemplo
                      </p>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        <PlaceholderAgentCard
                          name="Agente Captacion"
                          description="Especializado en la captacion y evaluacion de nuevos inmuebles para el inventario"
                          icon="home"
                          capabilities={["Captacion", "Evaluacion", "Negociacion"]}
                        />
                        <PlaceholderAgentCard
                          name="Agente Mantenimiento"
                          description="Gestiona solicitudes de mantenimiento y coordina reparaciones de inmuebles"
                          icon="briefcase"
                          capabilities={["Mantenimiento", "Coordinacion", "Seguimiento"]}
                        />
                        <PlaceholderAgentCard
                          name="Agente Juridico"
                          description="Asiste en procesos legales, contratos y aspectos juridicos de inmuebles"
                          icon="file"
                          capabilities={["Legal", "Contratos", "Asesoria"]}
                        />
                        <PlaceholderAgentCard
                          name="Agente Cartera"
                          description="Administra la cartera de clientes y hace seguimiento de pagos y cobros"
                          icon="dollar"
                          capabilities={["Cobranza", "Seguimiento", "Gestion de Cartera"]}
                        />
                        <PlaceholderAgentCard
                          name="Agente Servicio al Cliente"
                          description="Brinda atencion y soporte a los clientes, resolviendo dudas y gestionando solicitudes"
                          icon="user"
                          capabilities={["Atencion al cliente", "Soporte", "Resolucion de dudas"]}
                        />
                      </div>
                    </div>
                  </>
                )}
                {/* END TODO: Remove placeholder agents */}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="mb-4 text-gray-500">
                  No hay agentes disponibles para tu negocio.
                </p>
              </div>
            )}
          </>
        )}

        {/* Display system prompt when an agent is selected */}
        {selectedAgentId && (
          <div className="w-full rounded-md bg-white p-6 shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)]">
            {isLoadingAssistant ? (
              <Skeleton className="h-[50vh] rounded-md md:h-[70vh]" />
            ) : assistantDetails ? (
              <div className="flex flex-col gap-4">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="size-5 text-purple-500" />
                        <span className="text-base font-semibold">Instrucciones</span>
                      </div>
                      <div className="ml-7 flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-500">
                          <strong>version actual:</strong> {assistantDetails.version} -{" "}
                          {assistantDetails.description}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsVersionsModalOpen(true)}
                        >
                          Ver versiones
                        </Button>
                      </div>
                    </div>
                    {!isEditing ? (
                      <Button size="sm" onClick={handleEditClick}>
                        Editar
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={handleSave}
                          disabled={!hasChanges || !isValidPrompt}
                        >
                          Guardar
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancel}>
                          Cancelar
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-4 md:flex-row md:gap-6">
                    <div className="w-full min-w-0 md:flex-[2]">
                      <Textarea
                        value={isEditing ? editedPrompt : assistantDetails.context.systemPrompt}
                        onChange={(e) => setEditedPrompt(e.target.value)}
                        readOnly={!isEditing}
                        className={cn(
                          "h-[300px] min-h-[300px] resize-y font-mono text-sm p-4 md:h-[calc(100vh-350px)] md:min-h-[600px]",
                          isEditing
                            ? "border-blue-300 bg-white focus-visible:border-blue-500 focus-visible:ring-blue-500"
                            : "border-gray-200 bg-gray-50"
                        )}
                      />
                    </div>
                    <div className="w-full md:w-[280px] md:max-w-[320px]">
                      <p className="mb-4 text-base font-semibold">Herramientas</p>
                      <div className="flex flex-col gap-2">
                        {(() => {
                          // Rentals tool catalog (legacy default)
                          const rentalsTools = [
                            { value: "transferConversation", label: "Transferir Conversacion" },
                            { value: "scheduleVisit", label: "Agendar Visita" },
                            { value: "getAvailableSlots", label: "Obtener horarios disponibles" },
                            { value: "getProperty", label: "Obtener Propiedad" },
                            { value: "getPropertyByCode", label: "Obtener Propiedad por Codigo" },
                            { value: "searchProperties", label: "Buscar Propiedades" },
                            { value: "assignAgent", label: "Asignar Agente" },
                            { value: "setEmail", label: "Correo electrónico" },
                            {
                              value: "registerListingRequest",
                              label: "Registrar Solicitud de Publicacion",
                            },
                            { value: "discardLead", label: "Descartar Lead" },
                            { value: "calculateDaysFor", label: "Calcular Dias" },
                            {
                              value: "createMaintenanceRequest",
                              label: "Registrar Solicitud de Mantenimiento",
                            },
                            {
                              value: "getCustomerProperties",
                              label: "Obtener inmuebles del cliente",
                            },
                            {
                              value: "getCustomerDocuments",
                              label: "Obtener documentos del cliente",
                            },
                            { value: "getFloorPlan", label: "Obtener Plano de Piso" },
                            { value: "createServiceRequest", label: "Crear solicitud de servicio" },
                            { value: "updateServiceRequest", label: "Actualizar solicitud de servicio" },
                            {
                              value: "updatePipelinePosition",
                              label: "Actualizar estado del usuario",
                            },
                            {
                              value: "requestRentGuaranteePreApproval",
                              label: "Solicitar estudio de fianza",
                            },
                            {
                              value: "lookupByContractNumber",
                              label: "Buscar por Numero de Contrato",
                            },
                            {
                              value: "setAppointmentIntention",
                              label: "Registrar intencion de cita",
                            },
                            { value: "sendMedia", label: "Enviar multimedia" },
                          ];

                          // Development-v2 tool catalog
                          const developmentTools = [
                            { value: "transferConversation", label: "Transferir Conversacion" },
                            { value: "getProject", label: "Obtener Proyecto" },
                            { value: "searchProjects", label: "Buscar Proyectos" },
                            { value: "typeOfProject", label: "Tipo de Proyecto" },
                            { value: "scheduleVisit", label: "Agendar Visita" },
                            { value: "discardLead", label: "Descartar Lead" },
                            { value: "assignAgent", label: "Asignar Agente" },
                            {
                              value: "setAppointmentIntention",
                              label: "Registrar intencion de cita",
                            },
                            { value: "sendMedia", label: "Enviar multimedia" },
                            {
                              value: "updateLeadProfile",
                              label: "Actualizar perfil del lead",
                            },
                          ];

                          const toolsToShow =
                            merchantType === "development" ? developmentTools : rentalsTools;

                          return toolsToShow.map((tool) => (
                            <label key={tool.value} className="flex items-center gap-2">
                              <Checkbox
                                checked={selectedTools.includes(tool.value as ToolType)}
                                onCheckedChange={(checked) => {
                                  if (!isEditing) return;
                                  const toolValue = tool.value as ToolType;
                                  const newTools = checked
                                    ? [...selectedTools, toolValue]
                                    : selectedTools.filter((t) => t !== toolValue);
                                  setSelectedTools(newTools);
                                }}
                                disabled={!isEditing}
                                className={isEditing ? "" : "opacity-60"}
                              />
                              <span className={cn("text-sm", !isEditing && "opacity-60")}>
                                {tool.label}
                              </span>
                            </label>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-red-500">Error al cargar los detalles del agente</p>
            )}
          </div>
        )}

        {/* Versions Modal */}
        <Dialog open={isVersionsModalOpen} onOpenChange={setIsVersionsModalOpen}>
          <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-xl">
            <DialogHeader>
              <DialogTitle>Versiones del Asistente</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {isLoadingVersions ? (
                <div className="flex flex-col gap-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-[60px] w-full" />
                  ))}
                </div>
              ) : assistantVersions ? (
                isMobile ? (
                  <div className="flex flex-col">
                    {assistantVersions.map((version: any, index: number) => (
                      <div
                        key={version.version}
                        className={cn(
                          "p-3",
                          index < assistantVersions.length - 1 && "border-b border-gray-200"
                        )}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-sm font-semibold">v{version.version}</span>
                          <span className="text-xs text-gray-500">
                            {formatDateWithTimezone(version.created_at, country)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">
                          {version.description || "Sin descripcion"}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-2 text-left font-semibold text-gray-700">
                            Version
                          </th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-700">
                            Descripcion
                          </th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-700">
                            Fecha de Creacion
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {assistantVersions.map((version: any) => (
                          <tr key={version.version} className="border-b last:border-b-0">
                            <td className="px-4 py-2 font-semibold">{version.version}</td>
                            <td className="px-4 py-2">
                              {version.description || "Sin descripcion"}
                            </td>
                            <td className="px-4 py-2">
                              {formatDateWithTimezone(version.created_at, country)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                <p className="text-red-500">Error al cargar las versiones</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsVersionsModalOpen(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation Modal for Saving Changes */}
        <Dialog open={isConfirmModalOpen} onOpenChange={(open) => { if (!open) handleModalCancel(); }}>
          <DialogContent className="md:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirmar Cambios</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label>
                  Descripcion <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe los cambios realizados..."
                  maxLength={200}
                />
              </div>
              <div className="rounded-md border border-orange-200 bg-orange-50 p-3">
                <p className="text-sm text-orange-800">
                  Los cambios tendran impacto inmediato en el comportamiento del agente.
                </p>
              </div>
            </div>
            <DialogFooter>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleModalCancel}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmSave}
                  disabled={
                    !description.trim() || !editedPrompt.trim() || !hasChanges || updateAssistantMutation.isLoading
                  }
                >
                  {updateAssistantMutation.isLoading ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Confirmar"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Project Selection Modal (development merchants only) */}
        <Dialog open={isProjectModalOpen} onOpenChange={setIsProjectModalOpen}>
          <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-lg">
            <DialogHeader>
              <DialogTitle>Vincular Proyecto</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {(projectsList ?? []).length === 0 ? (
                <div className="py-8 text-center">
                  <p className="mb-4 text-gray-500">No hay proyectos disponibles.</p>
                  <p className="text-sm text-gray-400">
                    Crea un proyecto en Configuración → Proyectos.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {(projectsList ?? []).map((project) => {
                    const currentAgent = agentToLinkProject
                      ? (agents ?? []).find((a) => a.id === agentToLinkProject)
                      : undefined;
                    const isCurrentlyLinked = currentAgent?.projectId === project.id;
                    return (
                      <div
                        key={project.id}
                        className={cn(
                          "cursor-pointer rounded-md border p-4 transition-all hover:-translate-y-px",
                          isCurrentlyLinked
                            ? "border-palomma-primary bg-palomma-primary/5"
                            : "border-gray-200 hover:border-blue-500 hover:bg-blue-50"
                        )}
                        onClick={() => handleProjectSelect(project.id)}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex min-w-0 flex-col gap-1">
                            <span className="truncate text-base font-semibold">
                              {project.name}
                            </span>
                            <span className="truncate text-xs text-gray-500">
                              Código: {project.code}
                            </span>
                          </div>
                          {isCurrentlyLinked && (
                            <span className="shrink-0 text-xs font-semibold text-palomma-primary">
                              Vinculado
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                disabled={
                  updateAgentProjectMutation.isLoading ||
                  !(agentToLinkProject &&
                    (agents ?? []).find((a) => a.id === agentToLinkProject)?.projectId)
                }
                onClick={() => handleProjectSelect(null)}
              >
                Desvincular proyecto
              </Button>
              <Button variant="outline" onClick={() => setIsProjectModalOpen(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* WhatsApp Connection Selection Modal */}
        <Dialog open={isWhatsappModalOpen} onOpenChange={setIsWhatsappModalOpen}>
          <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-lg">
            <DialogHeader>
              <DialogTitle>Seleccionar Conexion de WhatsApp</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {isLoadingWhatsappConnections ? (
                <div className="flex flex-col gap-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-[60px] w-full" />
                  ))}
                </div>
              ) : whatsappConnections &&
                whatsappConnections.filter((connection) => connection.isPrimary === true).length >
                  0 ? (
                <div className="flex flex-col gap-3">
                  {whatsappConnections
                    .filter((connection) => connection.isPrimary === true)
                    .map((connection) => (
                      <div
                        key={connection.id}
                        className="cursor-pointer rounded-md border border-gray-200 p-4 transition-all hover:-translate-y-px hover:border-blue-500 hover:bg-blue-50"
                        onClick={() => handleWhatsappConnectionSelect(connection.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col gap-1">
                            <span className="text-base font-semibold">
                              {connection.phoneNumber || connection.phoneNumberId}
                            </span>
                            {connection.isPrimary && (
                              <span className="inline-flex w-fit items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
                                Principal
                              </span>
                            )}
                          </div>
                          <Plus className="size-5 text-blue-500" />
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="mb-4 text-gray-500">
                    No hay conexiones de WhatsApp disponibles.
                  </p>
                  <p className="text-sm text-gray-400">
                    Configura una conexion de WhatsApp en la seccion de configuracion.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                disabled={
                  updateWhatsappConnectionMutation.isLoading ||
                  !(agentToConnect &&
                    (agents ?? []).find((a) => a.id === agentToConnect)?.whatsappConnectionId)
                }
                onClick={() => handleWhatsappConnectionSelect(null)}
              >
                Desvincular número
              </Button>
              <Button variant="outline" onClick={() => setIsWhatsappModalOpen(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Migrate-to-V2 dialog. Reachable from any un-migrated agent card
            after the merchant has already flipped selfConfigEnabled (i.e. one
            of N agents was migrated and the others were left behind). */}
        <Dialog open={isMigrateModalOpen} onOpenChange={setIsMigrateModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar migración a V2</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                Esta acción creará un asistente en el grafo <code>development-v2</code> para
                este agente, congelando el prompt actual a partir del <code>agent.config</code>{" "}
                existente.
              </p>
              <p>¿Confirmas que quieres continuar?</p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsMigrateModalOpen(false)}
                disabled={migrateToV2Mutation.isLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmMigrate}
                disabled={migrateToV2Mutation.isLoading || !migratingAgentId}
              >
                {migrateToV2Mutation.isLoading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Migrando…
                  </>
                ) : (
                  "Migrar"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainSectionSection>
  );
};

export default AgentsConfigurationContainer;
